import { NumberFormatting, NumberService } from '@mews/framework';
import { RateExcludingProductsFormValues } from './types';

/**
 * Maps rate per night values to product order amounts.
 *
 * This function processes rate values for each night and transforms them into a format
 * suitable for product order pricing.
 *
 * @param perNight - The rate values per night from the form
 * @param minEditableUtc - Minimum editable UTC timestamp string, dates before this are excluded
 * @param currencyCode - The currency code for the rate amounts
 * @param localizationInfo - Information for formatting currency values
 * @returns An array of product order amounts with their corresponding IDs
 */
export const mapPerNight = (
    perNight: RateExcludingProductsFormValues['perNight'],
    minEditableUtc: string,
    currencyCode: string,
    localizationInfo: NumberFormatting
) => {
    // Transform eligible night rates into product order amounts
    return (
        perNight
            // Convert each night's value to product order amounts
            .flatMap((night) => {
                // Only include nights that:
                // - Are enabled
                // - Have a value
                // - Are not before the minimum editable date
                if (!night.enabled || night.value === '' || night.consumedUtc < minEditableUtc) {
                    return [];
                }

                const parsedAmount = {
                    value: NumberService.unformat(night.value, localizationInfo),
                    currencyCode,
                };

                // Create an entry for each product order ID with the same amount
                return night.productOrderIds.map((id) => ({
                    productOrderId: id,
                    amount: parsedAmount,
                }));
            })
    );
};

/**
 * Filters out unchanged price values from the edited per-night rates.
 *
 * If currency remains unchanged, only returns price items that differ from the initial values.
 * If currency has changed, returns all edited per-night rates without filtering.
 *
 * @param editedPerNight - The modified per-night rate values from the form
 * @param initialPerNight - The original per-night rate values
 * @param editedCurrency - The selected currency in the form
 * @param initialCurrency - The original currency
 * @returns Filtered array of per-night rate values containing only those that changed
 */
export const getFilteredPerNight = (
    editedPerNight: RateExcludingProductsFormValues['perNight'],
    initialPerNight: RateExcludingProductsFormValues['perNight'],
    editedCurrency: string,
    initialCurrency: string
): RateExcludingProductsFormValues['perNight'] => {
    return editedCurrency === initialCurrency
        ? editedPerNight.filter((edited, index) => {
              return edited.value !== initialPerNight[index].value;
          })
        : editedPerNight;
};
